import Modal from "patient-ping-remedy/packages/modal";
import Button, {StyleType} from "patient-ping-remedy/packages/button";
import {css} from "@emotion/css";
import EventConsumerForm from "@/components/pages/settings/event_consumers/EventConsumerForm";
import {createFormFieldConfig, useForm} from "@/hooks/useForm";
import {useContext, useEffect} from "react";
import Validate from "@/helpers/validate";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {CarecoApiContext} from "@/app-context/careco-api-context";
import {EventConsumer} from "@/api/dto/dto";
import {useAlertStore} from "@/store/alert_store";
import {ToastType} from "patient-ping-remedy/packages/toast";
import {AxiosError} from "axios";
import Helpers from "@/helpers/helpers";

interface CreateEventConsumerModalProps {
    close: () => void
}

const CreateEventConsumerModal = (props: CreateEventConsumerModalProps) => {
  const {carecoApi} = useContext(CarecoApiContext);
  const queryClient = useQueryClient();
  const {addAlert} = useAlertStore();
  const {form, setInitialForm, handleSubmit, handleChange, isValid, submitted} = useForm(submit);

  const {mutate, isPending} = useMutation({
    mutationFn: async (payload: EventConsumer[]) => carecoApi?.createEventConsumer(payload),
    onSuccess: () => {
      addAlert({content: "Event consumer created successfully.", type: ToastType.SUCCESS});
      queryClient.invalidateQueries({queryKey: ['eventConsumers']}).then();
      props.close();
    },
    onError: (error: AxiosError) => {
      Helpers.alertOnError(`Failed to create event consumer. ${Helpers.traceId(error)}`, error);
    }
  });

  useEffect(() => {
    setInitialForm({
      ...createFormFieldConfig(
        "name",
        '',
        {valid: false, message: ''},
        [Validate.required],
        true
      ),
      ...createFormFieldConfig(
        "filters",
        {},
        {valid: false, message: ''},
        [Validate.required],
        true
      )
    });
  }, [setInitialForm]);

  function submit() {
    const payload: EventConsumer[] = [{
      name: form.name.value,
      filters: form.filters.value,
    }];
    mutate(payload);
  }

  const modalButtons = [
    <Button
      key="createButton"
      styleType={StyleType.PRIMARY}
      onClick={handleSubmit}
      disabled={
        !isValid() || isPending || submitted
        || !form?.filters?.value?.eventStates || form.filters.value.eventStates.values.length === 0
        || !form?.filters?.value?.eventTypes || form.filters.value.eventTypes.values.length === 0
      }
    >Create</Button>,
    <Button key="cancelButton" styleType={StyleType.TERTIARY} onClick={props.close}>Cancel</Button>
  ];

  return (
    <Modal
      buttons={modalButtons}
      headerText={"Create Event Consumer"}
      className={css({ width: '70rem' })}
    >
      <EventConsumerForm form={form} handleChange={handleChange} isWithinModal={true}/>
    </Modal>
  );
};

export default CreateEventConsumerModal;
